import { type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { DropdownMenuCard } from 'components/Card';
import TreeComponent from 'components/TreeComponent';
import { useDebounce } from 'hooks/useDebounce';
import { isEmpty, uniq } from 'lodash';
import { parsedCustomerSignatureSelector } from 'slices/auth.slice';
import {
  organizationTreeSelector,
  selectExpandedTreeNodes,
  selectLeafNodes,
  selectOrganizationNodeLeaves,
  selectTreeMap,
  setExpandedTreeNodes,
} from 'slices/organizationHierarchy.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TOrganizationTreeNode } from '@timeedit/types';
import { Icon } from '@timeedit/ui-components';
import {
  Popover,
  type TreeProps,
  Typography,
} from '@timeedit/ui-components/antd';

export const FooterAddNewBtn = ({
  dataTestId = 'add-new-btn',
}: {
  dataTestId?: string;
}) => {
  return (
    <div className="te-mt-2">
      <Typography.Link data-testid={dataTestId}>
        Add
        <Icon
          id="DownOutlined"
          alt="Down"
          style={{ width: 10, marginLeft: 10 }}
        />
      </Typography.Link>
    </div>
  );
};

type TOrganizationTreePopoverProps = PropsWithChildren<{
  title: string;
  tooltip?: string;
  value?: (number | string)[];
  onChange: (val: (number | string)[]) => void;
  onlyLeafNodes?: boolean;
}>;

export const OrganizationTreePopover = ({
  title,
  tooltip,
  value,
  onChange,
  onlyLeafNodes = true,
  children = <FooterAddNewBtn />,
}: TOrganizationTreePopoverProps) => {
  const dispatch = useAppDispatch();

  const organizationNodes = useAppSelector(selectOrganizationNodeLeaves);
  const organizationTree = useAppSelector(organizationTreeSelector);
  const organizationTreeMap = useAppSelector(selectTreeMap);
  const expandedTreeNodes = useAppSelector(selectExpandedTreeNodes);
  const leafNodes = useAppSelector(selectLeafNodes);
  const customerSignature = useAppSelector(parsedCustomerSignatureSelector);

  const [searchValue, setSearchValue] = useState('');
  const debounceSearchValue = useDebounce(searchValue, 100);

  const onSelectNode = useCallback(
    (itemSelect: string[]) => {
      const selectedKeys = itemSelect.map((item) => item.split('#')[0]);
      onChange(uniq(selectedKeys));
    },
    [onChange]
  );

  const organizationNodeOptions = useMemo(
    () =>
      organizationNodes.map((node) => ({
        key: String(node.id),
        label: node.name,
        url: `/${customerSignature}/user-management/organizations?item1=${node.id}`,
      })),
    [organizationNodes, customerSignature]
  );

  const selectableNodeNames = useMemo(
    () =>
      Array.from(
        new Set(
          Object.keys(organizationTreeMap.ancestorsByName)
            .filter((name) =>
              name
                .toLocaleLowerCase()
                .includes(debounceSearchValue.toLocaleLowerCase())
            )
            .reduce<string[]>((acc, name) => {
              const names = [name].concat(
                organizationTreeMap.ancestorsByName[name]?.map((a) => a.name) ??
                  []
              );

              return acc.concat(names);
            }, [])
        )
      ),
    [debounceSearchValue, organizationTreeMap.ancestorsByName]
  );

  const treeData = useMemo(() => {
    if (isEmpty(organizationTree?.children)) {
      return [];
    }

    return convertDataPopup(
      organizationTree?.children,
      onlyLeafNodes,
      selectableNodeNames,
      new Set(leafNodes)
    );
  }, [leafNodes, onlyLeafNodes, organizationTree, selectableNodeNames]);

  const selectedKeys: string[] = useMemo(
    () =>
      value.flatMap((val: number | string) =>
        (organizationTreeMap.virtualIdsById[val] ?? []).map(
          (virtualId) => `${val}#${virtualId}`
        )
      ),
    [value, organizationTreeMap]
  );

  const handleExpand = useCallback<Exclude<TreeProps['onExpand'], undefined>>(
    (expandedKeys) => {
      dispatch(setExpandedTreeNodes(expandedKeys as string[]));
    },
    [dispatch]
  );

  const footer = useMemo(
    () => (
      <div>
        <Popover
          zIndex={10000}
          placement="bottomLeft"
          trigger={['click']}
          content={
            <TreeComponent
              onExpand={handleExpand}
              onSelect={onSelectNode}
              selectedKeys={selectedKeys.map(String)}
              treeData={treeData}
              multiple
              defaultExpandAll
              defaultExpandedKeys={expandedTreeNodes}
              handleSearch={setSearchValue}
            />
          }
        >
          <div>{children}</div>
        </Popover>
      </div>
    ),
    [
      children,
      expandedTreeNodes,
      handleExpand,
      onSelectNode,
      selectedKeys,
      treeData,
    ]
  );

  return (
    <DropdownMenuCard
      title={title}
      tooltip={tooltip}
      selectedKeys={value.map((val: number | string) => String(val))}
      options={organizationNodeOptions}
      onChange={onChange}
      footer={footer}
      data-testid="organization-node-select"
    />
  );
};

type TOrganizationTreeNodePopupData = {
  title: string;
  key: string;
  value: string;
  disabled?: boolean;
  children?: TOrganizationTreeNodePopupData[];
};

export const convertDataPopup = (
  tree: TOrganizationTreeNode[],
  onlyLeafNodes: boolean,
  selectableNodeNames: string[],
  leafNodeSet: Set<string>
): TOrganizationTreeNodePopupData[] => {
  return tree
    .filter((i) => selectableNodeNames.includes(i.name))
    .map((item) => {
      const children = item.children
        ? convertDataPopup(
            item.children,
            onlyLeafNodes,
            selectableNodeNames,
            leafNodeSet
          )
        : undefined;

      return {
        title: item.name,
        key: `${item.id}#${item.virtualId}`,
        value: `${item.id}#${item.virtualId}`,
        disabled: onlyLeafNodes && !leafNodeSet.has(item.name),
        children,
      } satisfies TOrganizationTreeNodePopupData;
    });
};
